import { Component, OnInit, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HeaderService } from '@core/services/header.service';
import { ApiService } from '@core/services/api.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { PermissionService } from '@core/services/permission.service';
import { ConfirmDialogService } from '@core/services/confirm-dialog.service';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'app-designations',
  templateUrl: './designations.component.html',
  styleUrls: ['./designations.component.scss']
})
export class DesignationsComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  designations: any[] = [];
  employees: any[] = [];
  loading = true;
  submitting = false;
  search = '';

  drawerOpen = false;
  editingId: string | null = null;
  form: any = { name: '', code: '', mailAlias: '', description: '' };

  constructor(
    private headerService: HeaderService,
    private apiService: ApiService,
    private snackbar: SnackbarService,
    public permissions: PermissionService,
    private confirmDialog: ConfirmDialogService
  ) {}

  ngOnInit(): void {
    this.headerService.setTitle('Designations');
    this.loadData();
  }

  loadData(): void {
    this.loading = true;
    forkJoin({
      designations: this.apiService.get('/designations').pipe(catchError(() => of([]))),
      employees: this.apiService.get('/employees').pipe(catchError(() => of([])))
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        this.designations = this.extractList(data.designations);
        this.employees = this.extractList(data.employees);
        this.loading = false;
      },
      error: () => { this.loading = false; }
    });
  }

  private extractList(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    return [];
  }

  private isActive(e: any): boolean {
    const s = (e?.status || '').toLowerCase();
    return s !== 'inactive' && s !== 'exited' && s !== 'exit' && s !== 'resigned' && s !== 'terminated';
  }

  getEmployeeCount(designation: any): number {
    const name = (designation?.name || '').toLowerCase();
    if (!name) return 0;
    return this.employees.filter(e => (e?.designation || '').toLowerCase() === name && this.isActive(e)).length;
  }

  get mergedDesignations(): any[] {
    const master = this.designations || [];
    const known = new Set(master.map(d => (d?.name || '').trim().toLowerCase()).filter(Boolean));
    const derived: any[] = [];
    const seen = new Set<string>();
    for (const e of this.employees) {
      const raw = (e?.designation || '').trim();
      const key = raw.toLowerCase();
      if (!raw || raw === '-' || known.has(key) || seen.has(key)) continue;
      seen.add(key);
      derived.push({ name: raw, code: null, mailAlias: null, description: null, unmanaged: true });
    }
    return [...master, ...derived.sort((a, b) => a.name.localeCompare(b.name))];
  }

  get filteredDesignations(): any[] {
    const q = this.search.trim().toLowerCase();
    const list = this.mergedDesignations;
    if (!q) return list;
    return list.filter(d =>
      (d.name || '').toLowerCase().includes(q) ||
      (d.code || '').toLowerCase().includes(q) ||
      (d.mailAlias || '').toLowerCase().includes(q)
    );
  }

  openDrawer(designation?: any): void {
    if (designation) {
      this.editingId = designation.id;
      this.form = {
        name: designation.name || '',
        code: designation.code || '',
        mailAlias: designation.mailAlias || '',
        description: designation.description || ''
      };
    } else {
      this.editingId = null;
      this.form = { name: '', code: '', mailAlias: '', description: '' };
    }
    this.drawerOpen = true;
  }

  closeDrawer(): void {
    this.drawerOpen = false;
  }

  submit(): void {
    if (!this.form.name?.trim()) { this.snackbar.showFailure('Designation name is required'); return; }
    this.submitting = true;
    const payload = {
      name: this.form.name.trim(),
      code: this.form.code?.trim() || null,
      mailAlias: this.form.mailAlias?.trim() || null,
      description: this.form.description?.trim() || null
    };
    const req = this.editingId
      ? this.apiService.patch('/designations/' + this.editingId, payload)
      : this.apiService.post('/designations', payload);

    req.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.snackbar.showSuccess(this.editingId ? 'Designation updated' : 'Designation created');
        this.submitting = false;
        this.drawerOpen = false;
        this.loadData();
      },
      error: (err: any) => {
        const msg = err?.error?.message || 'Failed to save designation';
        this.snackbar.showFailure(msg);
        this.submitting = false;
      }
    });
  }

  deleteDesignation(designation: any): void {
    this.confirmDialog.confirm(`"${designation.name}" will be permanently deleted.`, {
      title: 'Delete Designation', confirmLabel: 'Delete', danger: true
    }).then(ok => {
      if (!ok) return;
      this.apiService.delete('/designations/' + designation.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => { this.snackbar.showSuccess('Designation deleted'); this.loadData(); },
        error: () => this.snackbar.showFailure('Failed to delete designation')
      });
    });
  }
}
